import React, { useState } from 'react';
import styled from 'styled-components';
import { Palette } from 'core/config';
import { UseFormRegisterReturn } from 'react-hook-form';
import IMAGES from 'assets/images';

interface TypeChoiceProps
{
    name          : string;
    input         : UseFormRegisterReturn;
    initialValue? : string;
    setValue?     : (name : string, value : any) => void;
}

export const TypeChoice : React.FunctionComponent<TypeChoiceProps> = ({
    name,
    input,
    initialValue,
    setValue
}) =>
{
    const [ selectedTab, setSelectedTab ] = useState<string | undefined>(initialValue);

    const onClick = (tabId : string) =>
    {
        setValue && setValue(name, tabId);
        setSelectedTab(tabId);
    };

    return (
        <>
            <Paragraph>
                What type of mortgage are you applying for?
            </Paragraph>
            <ChoicesContainer>
                <TypeButton
                    image={IMAGES.icMortgagePreapproval}
                    title={'Pre-Approval'}
                    description={'You are currently shopping for a property and want to know what you qualify for'}
                    selected={selectedTab === 'p'}
                    onClick={() => onClick('p')}
                />
                <TypeButton 
                    image={IMAGES.icMortgageNew}
                    title={'New Purchase'}
                    description={'You have found a property you wish to arrange halal financing for'}
                    selected={selectedTab === 'n'}
                    onClick={() => onClick('n')}
                />
                <TypeButton 
                    image={IMAGES.icMortgageSingle}
                    title={'Transfer'}
                    description={'You have a property that you wish to arrange halal financing for'}
                    selected={selectedTab === 't'}
                    onClick={() => onClick('t')}
                />
            </ChoicesContainer>
            <HiddenInput {...input} />
        </>
    );
};

interface TypeButtonProps
{
    image       : string;
    title       : string;
    description : string;
    selected    : boolean;
    onClick     : () => void;
}

const HiddenInput = styled.input`
    visibility: hidden;
    position: absolute;
`;

const ChoicesContainer = styled.div`
    margin-top: 20px;
    margin-left: 39px;
    display: flex;
    flex-direction: column;
`;

const TypeButton : React.FunctionComponent<TypeButtonProps> = ({
    image, 
    title, 
    description, 
    selected, 
    onClick
}) => 
{
    return (
        <TypeContainer isSelected={selected} onClick={onClick}>
            <Image src={image} isSelected={selected} />
            <TextContent>
                <Title>{title}</Title>
                <Description>{description}</Description>
            </TextContent>
        </TypeContainer>
    );
};

const Image = styled.img<{ isSelected : boolean; }>`
    display: block;
    margin-left: auto;
    margin-bottom: 28px;
    margin-top: 15px;
    width: 45px;
    height: 45px;

    ${({ isSelected }) => isSelected ? 'filter: brightness(0) invert(1);' : ''}
`;

const TypeContainer = styled.div<{ isSelected: boolean; }>`
    border-radius: 10px;
    width: calc(100% - 80px);
    display: flex;
    flex-direction: row;
    font-family: 'SofiaPro-Regular';
    font-size: 12px;
    user-select: none;
    cursor: pointer;
    margin-left: 21px;
    padding-top: 18px;
    padding-bottom: 16px;
    margin-bottom: 14px;

    ${({ isSelected }) => isSelected 
        ? `
            background-color: ${Palette.ORANGE};
            color: ${Palette.EGGSHELL_WHITE};
        ` 
        : `
            background-color: ${Palette.EGGSHELL_WHITE};
            color: ${Palette.BLUE};
        `
}

    &:hover {
        background-color: ${Palette.ORANGE};
        color: ${Palette.EGGSHELL_WHITE};
    }

    &:hover ${Image} {
        filter: brightness(0) invert(1);
    }

    @media only screen and (min-width: 1300px) {
        font-size: 12px;
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;
    }

    :last-child {
        margin-bottom: -10px;
    }
`;

const TextContent = styled.div`
    width: 75%;
`;

const Title = styled.div`
    font-weight: 700;
    padding-left: 26px;
    padding-right: 25px;
`;

const Description = styled.div`
    font-weight: 100;
    padding-top: 6px;
    padding-left: 26px;
    padding-right: 20px;
`;

const Paragraph = styled.p`
    padding-top: 12px;
    padding-left: 60px;
    padding-right: 60px;
    font: normal normal normal 12px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    font-weight: 200;

    @media only screen and (min-width: 1300px) {
        font-size: 12px;

        span {
            font-size: 12px;
        }

        a {
            font-size: 12px;
        }
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;

        span {
            font-size: 14px;
        }

        a {
            font-size: 14px;
        }
    }
`;
